import React, { useEffect } from 'react';
import { useMst } from '../models/WithWineState';
import { useLocalStore, useObserver } from 'mobx-react-lite';
import { ProductPriceProps } from '../types/ProductPriceProps';
import styled from 'styled-components/macro';
import { TypeProduct } from '../types/TypeProduct';
import { parseBoolean } from '../utils/parseBoolean';

const UnstyledProductPrice: React.FC<ProductPriceProps> = ({
  className,
  showif,
  product_id,
  product: productProps,
  formatted,
  formatoptions,
  enablevariations,
}) => {
  const productId = product_id || productProps?.id;
  const stateTree = useMst();

  const state = useLocalStore(() => ({
    get isAuth() {
      return stateTree.session?.authenticated ?? false;
    },

    get product() {
      return productId ? stateTree.data.products.get(productId) : null;
    },

    get selectedVariation() {
      if (!parseBoolean(enablevariations, true)) {
        return state.product;
      }
      return (state.product?.selectedVariation ?? state.product) as TypeProduct;
    },

    get isGettingRemoteTotalPrice() {
      return state.selectedVariation?.isGettingRemoteTotalPrice;
    },

    get basedOn() {
      const variation = state.selectedVariation;
      if (variation) {
        if (
          variation.bottlePriceHigh !== null &&
          variation.minimumUnitPurchase !== null
        ) {
          return 'ww-price-bottle-minimum-purchase';
        }
        if (variation.singlePrice) {
          return 'ww-price-single';
        }
        if (variation.halfDozenPrice) {
          return 'ww-price-half-dozen';
        }
        if (variation.dozenPrice) {
          return 'ww-price-dozen';
        }
      }
      return 'ww-price-invalid';
    },

    get price() {
      const variation = state.selectedVariation;
      if (variation) {
        if (!state.isAuth) {
          if (
            variation.bottlePriceHigh !== null &&
            variation.minimumUnitPurchase !== null
          ) {
            return variation.bottlePriceHigh * variation.minimumUnitPurchase;
          }
          return (
            variation.singlePrice ??
            variation.halfDozenPrice ??
            variation.dozenPrice
          );
        } else if (typeof variation.remoteCalculatedTotalPrice === 'number') {
          return variation.remoteCalculatedTotalPrice;
        }
      }
      return null;
    },

    get formattedPrice() {
      const minimumFractionDigits = formatoptions?.minfractiondigits ?? 2;
      const maximumFractionDigits = formatoptions?.maxfractiondigits ?? 2;
      const local = formatoptions?.local ?? 'en-US';
      const currency = formatoptions?.currency ?? 'USD';
      return (
        state.price?.toLocaleString(local, {
          minimumFractionDigits,
          maximumFractionDigits,
          currency,
          style: 'currency',
        }) ?? ''
      );
    },
  }));

  useEffect(() => {
    if (
      state.product &&
      state.isAuth &&
      typeof state.isGettingRemoteTotalPrice !== 'boolean'
    ) {
      state.product.getRemoteCalculatedTotalPrice();
    }
  }, [state.product, state.isAuth, state.isGettingRemoteTotalPrice]);

  return useObserver(() => {
    if (
      (showif === 'loggedin' && !state.isAuth) ||
      (showif === 'loggedout' && state.isAuth) ||
      !productId
    ) {
      return null;
    }

    const hasNotYetFetchedCalculatedPrice =
      typeof state.isGettingRemoteTotalPrice !== 'boolean';
    const classes = [
      'ww-widget',
      'ww-product-price',
      state.basedOn,
      hasNotYetFetchedCalculatedPrice
        ? 'ww-price-not-fetching'
        : state.isGettingRemoteTotalPrice
        ? 'ww-price-fetching'
        : 'ww-price-fetched',
      state.isAuth
        ? state.isGettingRemoteTotalPrice === false
          ? typeof state.price === 'number'
            ? 'ww-price-ready'
            : 'ww-price-not-available'
          : 'ww-price-not-ready'
        : 'ww-price-ready',
      state.isAuth && hasNotYetFetchedCalculatedPrice
        ? 'ww-price-needs-update'
        : '',
      className ?? '',
    ];

    return (
      <span className={classes.join(' ')}>
        {formatted + '' === 'false' ? state.price : state.formattedPrice}
      </span>
    );
  });
};

export default styled(UnstyledProductPrice)`
  font-size: inherit;
  font-family: inherit;
`;
